/**
 * jw-product-variant.js
 * Phase F2.3 — Variant Domain Module
 *
 * 职责：SKU 查找、图片解析、选项匹配 — 纯 Variant 域逻辑
 *
 * 依赖：
 *   JWProductContext  (read-only: variants.byId, binding, skuImages)
 *   JWProductState    (write: activeSku)
 *   JWImageResolver   (image URL resolution)
 *
 * 🚫 禁止：
 *   - DOM gallery 操作 (setMain, thumbnail)
 *   - 价格计算引擎 (tierPrice)
 *   - tier card DOM 刷新 (refreshTierCards)
 *
 * Contract: docs/contracts/product_variant_v1.md
 */
(function () {
  'use strict';

  var _ctx   = null;
  var _state = null;

  /**
   * Initialize the variant module.
   *
   * @param {object} context — JWProductContext
   * @param {object} state   — JWProductState (flat mutable S)
   * @returns {object} JWProductVariant API
   */
  function init(context, state) {
    _ctx   = context || {};
    _state = state || {};
    return window.JWProductVariant;
  }

  /**
   * O(1) lookup SKU by ID.
   * Uses Context.variants.byId (F2.3), falls back to variants.list scan.
   *
   * @param {string} skuId
   * @returns {object|null} variant data
   */
  function lookupSku(skuId) {
    if (!skuId) return null;

    // Primary: Context.variants.byId
    var vars = _ctx.variants;
    if (vars && vars.byId) {
      var hit = vars.byId[skuId];
      if (hit) return hit;
    }

    // Fallback: linear scan (legacy, no Context)
    var list = (vars && vars.list) ? vars.list : [];
    for (var i = 0; i < list.length; i++) {
      if (list[i].id === skuId) return list[i];
    }
    return null;
  }

  /**
   * Extract raw option1 value from a SKU (1688 source value, NOT translated).
   * Compatible with both string[] and object[] options formats.
   *
   * @param {object} sku
   * @returns {string}
   */
  function getRawOption1(sku) {
    if (!sku || !sku.options || !sku.options.length) return '';
    var first = sku.options[0];
    if (typeof first === 'string') return first;
    if (first && typeof first === 'object') return first.value || '';
    return '';
  }

  /**
   * Resolve SKU image URL.
   * Uses JWImageResolver (F1.2) if available, falls back to sku.img/thumb.
   *
   * @param {string} skuId
   * @returns {string} image URL (empty string if not found)
   */
  function resolveImage(skuId) {
    var sku = lookupSku(skuId);
    if (!sku) return '';

    // Primary: JWImageResolver
    if (window.JWImageResolver && typeof window.JWImageResolver.resolve === 'function') {
      var result = window.JWImageResolver.resolve(
        sku,
        _ctx.binding ? _ctx.binding.imageBinding : null,
        _ctx.images ? _ctx.images.skuMap : {}
      );
      if (result) return result;
    }

    // Fallback: direct sku image
    return sku.img || sku.thumb || sku.image || '';
  }

  /**
   * Find image index in a list of main images.
   *
   * @param {string} skuId
   * @param {string[]} mainImages
   * @returns {number} index, or -1 if not found
   */
  function resolveImageIndex(skuId, mainImages) {
    var img = resolveImage(skuId);
    if (!img || !Array.isArray(mainImages)) return -1;
    return mainImages.indexOf(img);
  }

  /**
   * Select a variant by SKU ID.
   * Writes activeSku to State, returns resolved variant data for caller (DOM layer).
   *
   * @param {string} skuId
   * @returns {object} { sku, image, imageIndex }
   */
  function selectVariant(skuId) {
    if (_state) _state.activeSku = skuId;

    var sku     = lookupSku(skuId);
    var image   = resolveImage(skuId);
    var idx     = -1;

    // Note: mainImages comes from Context, not State
    var mainImgs = (_ctx.images && _ctx.images.main) ? _ctx.images.main : [];
    if (image) idx = mainImgs.indexOf(image);

    return {
      sku:        sku || null,
      image:      image,
      imageIndex: idx,
      options:    sku ? (Array.isArray(sku.options) ? sku.options.slice() : []) : []
    };
  }

  /**
   * Get all option values for a SKU.
   *
   * @param {string} skuId
   * @returns {string[]}
   */
  function getOptions(skuId) {
    var sku = lookupSku(skuId);
    if (!sku || !Array.isArray(sku.options)) return [];
    return sku.options.slice();
  }

  /**
   * Check if a SKU exists in the Context.
   *
   * @param {string} skuId
   * @returns {boolean}
   */
  function exists(skuId) {
    return lookupSku(skuId) !== null;
  }

  // ── Expose ──
  window.JWProductVariant = {
    init:              init,
    lookupSku:         lookupSku,
    resolveImage:      resolveImage,
    resolveImageIndex: resolveImageIndex,
    selectVariant:     selectVariant,
    getOptions:        getOptions,
    getRawOption1:     getRawOption1,
    exists:            exists
  };

})();
